import { Routes } from '@angular/router';
import { ScanPageComponent } from '@pages/scan-page/scan-page.component';
import { BaseLayoutComponent } from '@layouts/base/base.layout.component';
import { EnsureUserIsAuthenticated } from '@guard/ensure-user-authenticated';
import { ensureOnlineGuard } from '@guard/ensure-online.guard';
import { ensureScanAccessGuard } from '@guard/ensure-scan-access.guard';
import { sessionOpenGuard } from '@guard/session-open.guard';
import { hasInstanceGuard } from '@guard/has-instance.guard';
import { ensureTenantMatchGuard } from '@guard/ensure-tenant-match.guard';
import { LoginPageComponent } from '@pages/login-page/login-page.component';
import { ResetPasswordRequestPageComponent } from '@pages/reset-password-request-page/reset-password-request-page.component';
import { ResetPasswordPageComponent } from '@pages/reset-password-page/reset-password-page.component';
import { OpenSessionPageComponent } from '@pages/open-session-page/open-session-page.component';
import { CloseSessionPageComponent } from '@pages/close-session-page/close-session-page.component';
import { ChooseOrderTypePageComponent } from '@pages/choose-order-type-page/choose-order-type-page.component';
import { OrderHeaderComponent } from '@layouts/order-header/order-header.component';
import { AppRoutes } from '@helpers/app-routes';
import { ChooseMealPlanPageComponent } from '@pages/choose-meal-plan-page/choose-meal-plan-page.component';
import { chooseOrderTypeResolver } from '@pages/choose-order-type-page/choose-order-type.resolver';
import { chooseMealPlanResolver } from '@pages/choose-meal-plan-page/choose-meal-plan.resolver';
import { OrderPageComponent } from '@pages/order-page/order-page.component';
import { orderMenuResolver } from '@pages/order-page/order-menu.resolver';
import { OrderListPageComponent } from '@pages/order-list-page/order-list-page.component';
import { OrderDetailPageComponent } from '@pages/order-detail-page/order-detail-page.component';
import { OrderSummaryPageComponent } from '@pages/order-summary-page/order-summary-page.component';
import { SessionListPageComponent } from '@pages/session-list-page/session-list-page.component';
import { SettingsPageComponent } from '@pages/settings-page/settings-page.component';
import { ChooseInstancePageComponent } from '@pages/choose-instance-page/choose-instance-page.component';
import { AdminSettingsPageComponent } from '@pages/admin-settings-page/admin-settings-page.component';

export const routes: Routes = [
  { path: '', redirectTo: AppRoutes.login, pathMatch: 'full' },
  {
    path: '',
    component: BaseLayoutComponent,
    children: [
      {
        path: AppRoutes.login,
        component: LoginPageComponent,
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: false,
            showDeliveryButton: false,
            showPreferencesButton: true,
            showSettingsButton: false,
          },
        },
      },
      {
        path: AppRoutes.resetPasswordRequest,
        component: ResetPasswordRequestPageComponent,
        canActivate: [ensureOnlineGuard],
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: false,
            showDeliveryButton: false,
            showPreferencesButton: true,
            showSettingsButton: false,
          },
        },
      },
      {
        path: `${AppRoutes.resetPassword}/:token`,
        component: ResetPasswordPageComponent,
        canActivate: [ensureOnlineGuard],
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: false,
            showDeliveryButton: false,
            showPreferencesButton: true,
            showSettingsButton: false,
          },
        },
      },
    ],
  },
  {
    path: '',
    canActivate: [EnsureUserIsAuthenticated, ensureTenantMatchGuard],
    component: BaseLayoutComponent,
    children: [
      {
        path: AppRoutes.openSession,
        component: OpenSessionPageComponent,
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: AppRoutes.settings,
        component: SettingsPageComponent,
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: AppRoutes.sessionList,
        component: SessionListPageComponent,
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: AppRoutes.chooseInstance,
        component: ChooseInstancePageComponent,
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: AppRoutes.adminSettings,
        component: AdminSettingsPageComponent,
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
    ],
  },
  {
    path: '',
    canActivate: [ensureScanAccessGuard, ensureTenantMatchGuard, hasInstanceGuard, sessionOpenGuard],
    component: BaseLayoutComponent,
    children: [
      {
        path: AppRoutes.scanPage,
        component: ScanPageComponent,
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
    ],
  },
  {
    path: '',
    canActivate: [EnsureUserIsAuthenticated, ensureTenantMatchGuard, hasInstanceGuard, sessionOpenGuard],
    component: BaseLayoutComponent,
    children: [
      {
        path: AppRoutes.closeSession,
        component: CloseSessionPageComponent,
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: AppRoutes.orderList,
        component: OrderListPageComponent,
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: `${AppRoutes.orderDetail}/:id`,
        component: OrderDetailPageComponent,
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: '',
        component: OrderHeaderComponent,
        children: [
          {
            path: AppRoutes.chooseOrderType,
            component: ChooseOrderTypePageComponent,
            resolve: { modes: chooseOrderTypeResolver },
            data: {
              header: {
                showLocation: true,
                showCashRegister: true,
                showStatus: true,
                showUserMenu: true,
                showDeliveryButton: true,
                showPreferencesButton: true,
                showSettingsButton: true,
              },
            },
          },
          {
            path: AppRoutes.chooseMealPlan,
            component: ChooseMealPlanPageComponent,
            resolve: { mealPlans: chooseMealPlanResolver },
            data: {
              header: {
                showLocation: true,
                showCashRegister: true,
                showStatus: true,
                showUserMenu: true,
                showDeliveryButton: true,
                showPreferencesButton: true,
                showSettingsButton: true,
              },
            },
          },
          {
            path: AppRoutes.order,
            component: OrderPageComponent,
            resolve: { menu: orderMenuResolver },
            data: {
              header: {
                showLocation: true,
                showCashRegister: true,
                showStatus: true,
                showUserMenu: true,
                showDeliveryButton: true,
                showPreferencesButton: true,
                showSettingsButton: true,
              },
            },
          },
          {
            path: AppRoutes.orderSummary,
            component: OrderSummaryPageComponent,
            data: {
              header: {
                showLocation: true,
                showCashRegister: true,
                showStatus: true,
                showUserMenu: true,
                showDeliveryButton: true,
                showPreferencesButton: true,
                showSettingsButton: true,
              },
            },
          },
        ],
      },
    ],
  },
];
